
import { defineComponent, reactive, toRefs, onMounted, computed, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import chipSituationApi from '@/api/chipSituation'
import ws from '@/websocket/index'
import { maskStockName, maskStockCode } from '@/views/smallAmount/components/tableColumns'
import globalsets from '@/utils/globalsets'
import ChipSituationTrend from './components/ChipSituationTrend.vue'
import ChipDistributionAnalysis from './components/ChipDistributionAnalysis.vue'
import { MESSAGEID_DEF } from '@/websocket/quotationWebsocket'

export default defineComponent({
  name: 'ChipSituation',
  components: {
    ChipSituationTrend,
    ChipDistributionAnalysis
  },
  setup() {
    const route = useRoute()

    // 清理 symbol，移除可能存在的空格或加号等无效字符
    const getCleanSymbol = (code: string | undefined | null) => {
      if (!code) return ''
      return code.replace(/[\s+]+/g, '')
    }

    const state = reactive({
      // ||(route.query.theme as string)
      theme: 'light',
      symbol: getCleanSymbol(route.query.code as string) || '300750',
      market: (route.query.market as string) || 'sz',
      token: (route.query.token as string) || '',
      dataMask: false,
      situationData: {} as any,
      jzdData: {} as any,
      stockInfo: {
        code: getCleanSymbol(route.query.code as string) || '300750',
        name: '--',
        lastPrice: 0 as number | null,
        riseAndFall: 0 as number | null,
        increase: 0 as number | null,
        preClosePrice: 0,
        isSuspended: false
      }
    })

    // 判断是否为美股市场
    const isUSMarket = computed(() => {
      const marketLower = String(state.market || '').toLowerCase()
      return marketLower === 'nyse' || marketLower === 'amex' || marketLower === 'nasdaq' || marketLower === 'na'
    })

    // 判断是否为港股市场
    const isHKMarket = computed(() => {
      const marketLower = String(state.market || '').toLowerCase()
      return marketLower === 'hkse' || marketLower === 'hkex' || state.market === 'HK'
    })

    // 设置延时标记
    const isDelayHK = computed(() => {
      if (isHKMarket.value) {
        return route.query.HKDELAY == '0'
      }
      return false
    })

    // 格式化价格显示
    const getFormattedPrice = (price: number | null | undefined) => {
      if (price === null || price === undefined) return '—'
      if (isUSMarket.value) {
        return globalsets.getUSPrice(price)
      } else if (isHKMarket.value) {
        return globalsets.getHKPrice(price)
      }
      return globalsets.getNumber2(price)
    }

    // 格式化涨跌显示
    const getFormattedChange = (change: number) => {
      if (change === null || change === undefined) return '—'
      if (isUSMarket.value) {
        const price = state.stockInfo.lastPrice || 0
        return globalsets.getUSChange(change, price)
      } else if (isHKMarket.value) {
        return globalsets.getHKChange(change)
      }
      return globalsets.getNumber2(change)
    }

    // 格式化涨跌幅显示
    const getFormattedChangePercent = (changePercent: number) => {
      console.log('changePercent', changePercent)
      if (changePercent === null || changePercent === undefined) return '—'
      if (isUSMarket.value) {
        return globalsets.getUSChangePercent(changePercent)
      } else if (isHKMarket.value) {
        return globalsets.getHKChangePercent(changePercent)
      }
      return (changePercent > 0 ? '+' : '') + globalsets.getPencent(changePercent)
    }

    const mcnMode = route.query.mcnMode === '1'

    const displayCode = computed(() =>
      mcnMode ? maskStockCode(state.stockInfo.code) : state.stockInfo.code
    )

    const displayName = computed(() =>
      mcnMode ? maskStockName(state.stockInfo.name) : state.stockInfo.name
    )



    const displayLastPrice = computed(() => {
      return getFormattedPrice(state.stockInfo.lastPrice)
    })

    // 专门给顶部的股票价格箭头使用
    const colorClass = (val: number | null | undefined) => {
      if (val === null || val === undefined || val === 0) return ''
      return val > 0 ? 'red' : 'green'
    }

    const initData = async () => {
      try {
        const reqData = {
          symbol: state.symbol,
          market: state.market,
          token: state.token
        }

        const [res1, res2] = await Promise.all([
          chipSituationApi.getSituation(reqData),
          chipSituationApi.getJzd(reqData)
        ])

        if (res1 && (res1 as any).code === 1) {
          state.situationData = (res1 as any).data || {}
          if (state.situationData.name) {
            state.stockInfo.name = state.situationData.name
          }
          if (state.situationData.dataMask !== undefined) {
            state.dataMask = state.situationData.dataMask
          }
        }

        if (res2 && (res2 as any).code === 1) {
          state.jzdData = (res2 as any).data || {}
          if (state.jzdData.name) {
            state.stockInfo.name = state.jzdData.name
          }
          if (state.jzdData.dataMask !== undefined) {
            state.dataMask = state.jzdData.dataMask
          }
        }

        // 订阅行情
        subscribeQuote()
      } catch (error) {
        console.error('获取筹码态势数据失败', error)
      }
    }

    const dataTool = ref(null) as any

    const subscribeQuote = () => {
      // 通过websocket订阅个股行情，参考navtopF10.vue
      const marketRaw = String(state.market || '')
      const exch = isDelayHK.value ? 'DELAY_' + marketRaw : marketRaw

      const marketLower = marketRaw.toLowerCase()
      const now = new Date()
      const hours = now.getHours()
      if (hours >= 16) {
        if (marketLower === 'nyse' || marketLower === 'amex' || marketLower === 'nasdaq' || marketLower === 'na') {
          dataTool.value = MESSAGEID_DEF.MSGID_USSTOCK_HQ_SNAP_REG
        }
      }

      ws.websocket.safeSubscribeDYNA(state.symbol, exch, dataTool.value)
      ws.websocket.on('onMessage', handleMessage)
    }

    const handleMessage = (msg: any) => {
      const _data = msg.QuoteData
      const isCurrentStock = msg.Inst == state.symbol
      if (!isCurrentStock) return

      if (_data && _data.StatisticsData && (_data.StatisticsData || []).length > 0) {
        state.stockInfo.preClosePrice = _data.StatisticsData[0].PreClosePrice
      }

      if (_data && _data.StaticData && (_data.StaticData || []).length > 0) {
        state.stockInfo.name = _data.StaticData[0].InstrumentName
      }

      if (_data && _data.DynaData && (_data.DynaData || []).length > 0) {
        state.stockInfo.lastPrice = _data.DynaData[0].LastPrice || null
      }

      let instStatus = 0
      if (_data && (_data.InstStatusData || [])[0]) {
        instStatus = ((msg.QuoteData.InstStatusData || [])[0] || {}).StatusType
      }

      // 停牌
      if (instStatus === 6) {
        state.stockInfo.isSuspended = true
        state.stockInfo.riseAndFall = null
        state.stockInfo.increase = null
        state.stockInfo.lastPrice = null
      } else {
        state.stockInfo.isSuspended = false
        if (state.stockInfo.lastPrice && state.stockInfo.preClosePrice) {
          state.stockInfo.riseAndFall = state.stockInfo.lastPrice - state.stockInfo.preClosePrice
          state.stockInfo.increase = (state.stockInfo.riseAndFall / state.stockInfo.preClosePrice) * 100
        }
      }
    }

    onMounted(() => {
      initData()
    })

    onUnmounted(() => {
      if (ws.websocket) {
        ws.websocket.safeUnsubscribeDYNA(state.symbol, state.market)
        ws.websocket.off('onMessage', handleMessage)
      }
    })

    return {
      ...toRefs(state),
      displayCode,
      displayName,
      displayLastPrice,
      isDelayHK,
      getFormattedChange,
      getFormattedChangePercent,
      colorClass
    }
  }

})
